import React, {useState} from "react";
import {Popconfirm, Tooltip} from "antd";
import {
    DeleteOutlined,
    FieldTimeOutlined,
    CheckCircleOutlined,
    CloseCircleOutlined
} from "@ant-design/icons";
import {useTranslation} from "react-i18next";
import moment from "moment";
import {CustomButton} from "../../_defaultComponents/CustomButton/CustomButton";
import {useStore} from "../../../hooks/useStore";
import DataTablePropTypes from "./ComponentPropTypes";
import {TableContainer, ActionButtons} from "./ComponentStyles";
import UpdateModal from "./UpdateModal/UpdateModal";
import {CourierLink} from "../../_defaultComponents/CourierLink/CourierLink";
import {EmptyMessage} from "../../_defaultComponents/EmptyMessage/EmptyMessage";

const DataTable = ({
    data,
    setData,
    loading,
    setReloadData,
    currentPage,
    setCurrentPage,
    dataCount
}: DataTablePropTypes) => {
    const {t} = useTranslation(["dayOff", "dayOffStatuses"]);
    const stores = useStore();
    const [deleteConfirmLoading, setDeleteConfirmLoading] = useState(false);
    const [selectedItem, setSelectedItem] = useState(null);
    const [approveLoading, setApproveLoading] = useState(false);

    const onClickDeleteButton = async id => {
        stores.appStore.openConfirmModal({
            title: t("modal.confirmTitle", {ns: "common"}),
            content: t("other.dayOffDeleteConfirmContent", {ns: "common"}),
            onOk: async () => {
                setDeleteConfirmLoading(id);
                await stores.shiftStore.deleteDayOff({_id: id});
                setReloadData();
                setDeleteConfirmLoading(false);
            }
        });
    };

    const closeUpdateModal = (reloadData = false) => {
        if (reloadData) setReloadData();
        setSelectedItem();
    };

    const approveDayOff = async id => {
        setApproveLoading(true);
        const result = await stores.shiftStore.updateDayOffApproval({
            dayOffId: id,
            approval: "approved"
        });
        if (result) {
            setData({
                rows: data.map(d => {
                    if (d._id.toString() === id.toString()) {
                        d.approveStatus = "approved";
                    }
                    return d;
                }),
                dataCount
            });
        }
        setApproveLoading(false);
    };

    const rejectDayOff = async id => {
        setApproveLoading(true);
        const result = await stores.shiftStore.updateDayOffApproval({
            dayOffId: id,
            approval: "rejected"
        });
        if (result) {
            setData({
                rows: data.map(d => {
                    if (d._id.toString() === id.toString()) {
                        d.approveStatus = "rejected";
                    }
                    return d;
                }),
                dataCount
            });
        }
        setApproveLoading(false);
    };

    const renderActionButtons = (id, item) => {
        const canUpdate = item?.date ? moment(item.date) >= moment() : false;

        const permisions = {
            update:
                canUpdate &&
                (!item.approveStatus || ["waitingApprove"].includes(item.approveStatus)),
            approve:
                canUpdate &&
                (!item.approveStatus || ["waitingApprove"].includes(item.approveStatus)),
            reject:
                canUpdate &&
                (!item.approveStatus || ["waitingApprove"].includes(item.approveStatus)),
            delete:
                canUpdate &&
                (!item.approveStatus || ["waitingApprove"].includes(item.approveStatus))
        };
        return (
            <ActionButtons>
                {permisions.delete && (
                    <Tooltip title={t("actions.delete", {ns: "common"})}>
                        <CustomButton
                            data-testid="deleteButton"
                            disabled={deleteConfirmLoading}
                            loading={deleteConfirmLoading === id}
                            onClick={() => onClickDeleteButton(id)}
                            icon={<DeleteOutlined />}
                        ></CustomButton>
                    </Tooltip>
                )}
                {permisions.update && (
                    <Tooltip title={t("actions.update", {ns: "common"})}>
                        <CustomButton
                            data-testid="updateButton"
                            disabled={deleteConfirmLoading}
                            loading={deleteConfirmLoading === id}
                            onClick={() => setSelectedItem(item)}
                            icon={<FieldTimeOutlined />}
                        ></CustomButton>
                    </Tooltip>
                )}

                {permisions.approve && (
                    <Tooltip title={t("dayOff.actions.approveDayOff", {ns: "dayOff"})}>
                        <Popconfirm
                            title={t("modal.confirmTitle", {ns: "common"})}
                            open={open}
                            onConfirm={async () => {
                                await approveDayOff(item._id);
                            }}
                            onCancel={() => setOpen(false)}
                            okText={t("modal.yesButton", {ns: "common"})}
                            cancelText={t("modal.noButton", {ns: "common"})}
                        >
                            <CustomButton
                                loading={approveLoading && selectedItem?._id === id}
                                data-testid="objectionButton"
                                icon={<CheckCircleOutlined />}
                            ></CustomButton>
                        </Popconfirm>
                    </Tooltip>
                )}
                {permisions.reject && (
                    <Tooltip title={t("dayOff.actions.rejectDayOff", {ns: "dayOff"})}>
                        <Popconfirm
                            title={t("modal.confirmTitle", {ns: "common"})}
                            open={open}
                            onConfirm={async () => {
                                await rejectDayOff(item._id);
                            }}
                            onCancel={() => setOpen(false)}
                            okText={t("modal.yesButton", {ns: "common"})}
                            cancelText={t("modal.noButton", {ns: "common"})}
                        >
                            <CustomButton
                                loading={approveLoading && selectedItem?._id === id}
                                data-testid="objectionButton"
                                icon={<CloseCircleOutlined />}
                            ></CustomButton>
                        </Popconfirm>
                    </Tooltip>
                )}
            </ActionButtons>
        );
    };

    return (
        <>
            <UpdateModal
                id={selectedItem?._id}
                courierName={selectedItem?.courier?.fullname}
                visibility={Boolean(selectedItem?._id)}
                date={moment(selectedItem?.date)}
                onCancel={closeUpdateModal}
            />
            <TableContainer
                pagination={{
                    total: dataCount,
                    current: currentPage,
                    onChange: async page => {
                        setCurrentPage(page);
                    },
                    hideOnSinglePage: true,
                    showSizeChanger: false
                }}
                rowKey="_id"
                dataSource={data}
                data-testid="shiftTable"
                locale={{
                    emptyText: <EmptyMessage />
                }}
                loading={loading}
                scroll={{x: "max-content"}}
            >
                <TableContainer.Column
                    dataIndex="date"
                    title={t("fields.date", {ns: "common"})}
                    render={date => (
                        <span data-testid="date">
                            {!date ? "-" : moment(date).format("DD.MM.YYYY")}
                        </span>
                    )}
                />
                <TableContainer.Column
                    dataIndex="courier"
                    title={t("fields.courier", {ns: "common"})}
                    render={courier => (
                        <span data-testid="date">
                            {stores.meStore.checkRole("courierDetail") ? (
                                <CourierLink target="_blank" to={`/couriers/${courier?._id}`}>
                                    {courier?.fullname}
                                </CourierLink>
                            ) : (
                                courier?.fullname
                            )}
                        </span>
                    )}
                />
                <TableContainer.Column
                    dataIndex="courier"
                    title={t("fields.identificationNumber", {ns: "common"})}
                    render={courier => <span data-testid="date">{courier?.tckn}</span>}
                />
                <TableContainer.Column
                    dataIndex="courier"
                    title={t("fields.city", {ns: "common"})}
                    render={courier => <span data-testid="date">{courier?.city.capitalize()}</span>}
                />
                <TableContainer.Column
                    dataIndex="approveStatus"
                    title={t("fields.status", {ns: "common"})}
                    render={approveStatus => (
                        <span data-testid="date">{t(approveStatus, {ns: "dayOffStatuses"})}</span>
                    )}
                />
                <TableContainer.Column
                    align="right"
                    title={t("actions.action", {ns: "common"})}
                    dataIndex="_id"
                    render={renderActionButtons}
                />
            </TableContainer>
        </>
    );
};

export default DataTable;
